import { DataRequestStatusEnum, RequestedStudyTypeEnum, StudyRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IDataRequest, IRequestedStudy, IRequestedStudyModification } from '@vivli/features/data-requests/infrastructure/interface';
import { useRequestedStudyPermissions } from './use-requested-study-permissions.hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useAssignedAppTypeContext, useDataRequestPermissionsContext } from '@vivli/core/infrastructure/context';

export function useRequestedStudyBehavior() {
    const { userIsDataProviderOrgAdmin } = useRequestedStudyPermissions();
    const { isAmr } = useAssignedAppTypeContext();
    const dataRequestPermissions = useDataRequestPermissionsContext();

    const optionsConstants = {
        thisRequestOnlyIpd: 'tro',
        downloadable: 'downloadable',
        skipIrpReview: 'skip',
    };

    const optionOnTitlesConstants = {
        thisRequestOnlyIpd: 'Data loaded for this request only',
        downloadable: 'Data package downloadable                                    ',
        skipIrpReview: 'Bypass IRP Review          ',
    };

    const optionOffTitlesConstants = {
        thisRequestOnlyIpd: 'Data loaded from stored data     ',
        downloadable: 'Data available in secure research environment only',
        skipIrpReview: 'Include standard IRP Review',
    };

    const statusConstants = {
        dataPackageProvided: 'Data Package Provided to Requester',
        awaitingValidation: 'Awaiting DUA Validation before Data Package Upload',
        validatedAndReadyForUpload: 'Data to be provided by Data Contributor',
        willUseExistingTemplateData: 'Data already on platform',
        needsLoadingAfterApproval: 'Data to be loaded after approval',
    };

    const getStudyIdForDisplay = (requestedStudy: IRequestedStudy) => {
        let displayStudyId = requestedStudy.nctId ? requestedStudy.nctId : requestedStudy.sponsorProtocolId;
        //case below for unlisted studies
        displayStudyId = displayStudyId !== 'false' ? displayStudyId : requestedStudy.sponsorProtocolId;
        return displayStudyId;
    };

    //returns messages customized to current status and user;
    //most of these are for users who don't have rights to upload data
    //in cases not shown, message remains null
    const getStatusMessage = (
        activeRequestedStudy: IRequestedStudy,
        activeDataRequest: IDataRequest,
        dataPackageStatus: boolean,
        user: any
    ) => {
        let msg = null;
        const canUpload = dataRequestPermissions.canUpload(activeDataRequest, activeRequestedStudy);
        const requestedStudyHasData = activeRequestedStudy.studyRequestStatus === StudyRequestStatusEnum.IPDProvided;
        const uploadNeededButNotByMe = !canUpload && !requestedStudyHasData;

        if (requestedStudyHasData) {
            return statusConstants.dataPackageProvided;
        }

        switch (activeDataRequest.status) {
            case DataRequestStatusEnum.Draft:
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.AwaitingIRPApproval:
                if (uploadNeededButNotByMe) {
                    msg = dataPackageStatus ? statusConstants.willUseExistingTemplateData : statusConstants.needsLoadingAfterApproval;
                }
                break;
            case DataRequestStatusEnum.Approved:
            case DataRequestStatusEnum.AwaitingDUAValidation:
                if (uploadNeededButNotByMe) {
                    msg = statusConstants.awaitingValidation;
                }
                break;
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.PartiallyFulfilled:
                if (uploadNeededButNotByMe) {
                    msg = statusConstants.validatedAndReadyForUpload;
                }
                break;
            default:
                break;
        }

        return msg;
    };

    const canEditAdvancedOptions = (activeDataRequest: IDataRequest, activeRequestedStudy: IRequestedStudy, user: any) => {
        let canEdit = false;
        const isVivliAdmin = user?.vivliPermissions?.includes(PermissionsEnum.DrActAdv) ?? false;
        const unlistedStudy = activeRequestedStudy.requestedStudyType === RequestedStudyTypeEnum.Unlisted;
        const isAuthorizedOptionsEditor = userIsDataProviderOrgAdmin(activeRequestedStudy);
        //NOTE - no longer allowing dataset owners to edit advanced options  || userIsDatasetOwner(activeRequestedStudy);
        //TODO PERMFE -replace to permissions+
        if (activeRequestedStudy.studyRequestStatus === StudyRequestStatusEnum.IPDProvided || (unlistedStudy && !isVivliAdmin)) {
            return canEdit;
        }

        switch (activeDataRequest.status) {
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.Approved:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.DUAReturnedToRequester:
            case DataRequestStatusEnum.PartiallyFulfilled:
                //TODO PERMFE -replace to permissions+
                canEdit = isVivliAdmin || isAuthorizedOptionsEditor;
                break;
        }

        return canEdit;
    };

    const canShowAdvancedOptionsStatus = (activeDataRequest: IDataRequest) => {
        let canShow = false;
        switch (activeDataRequest.status) {
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.Approved:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.DUAReturnedToRequester:
            case DataRequestStatusEnum.PartiallyFulfilled:
            case DataRequestStatusEnum.Fulfilled:
                canShow = true;
                break;
        }
        return canShow;
    };

    const canChangeIrpReview = (activeDataRequest: IDataRequest, activeRequestedStudy: IRequestedStudy, isVivliAdmin: boolean) => {
        let canEdit = false;
        switch (activeDataRequest.status) {
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.SubmittedToVivli:
                canEdit = isVivliAdmin;
                break;
        }

        return canEdit;
    };

    const displayIrpReviewSelection = (activeDataRequest: IDataRequest) => {
        let canShow = false;
        switch (activeDataRequest.status) {
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.SubmittedToVivli:
                canShow = true;
                break;
        }
        return canShow;
    };

    const getOptionsToModify = (requestedStudy: IRequestedStudy, dataRequest: IDataRequest, isVivliAdmin: boolean, options: string[]) => {
        const isThisRequestOnly = options.includes(optionsConstants.thisRequestOnlyIpd);

        //only vivli admin in CT mode allowed to see an isDownloadable checkbox,
        //so don't make any change for non-vivli admin
        //AND don't make any changes for vivli admin in AMR mode
        const isDownloadable =
            isVivliAdmin && !isAmr
                ? options.includes(optionsConstants.downloadable)
                : requestedStudy.downloadableRequestedStudyIPDDataPackage;

        //what's the current value for bypass IRP review?
        let isBypassIrpReview = requestedStudy.bypassIRPReview;

        if (isVivliAdmin) {
            //for vivli admins, check options selected
            //is there a selected option for bypass IRP review?
            const skipOptionSelected = options.includes(optionsConstants.skipIrpReview);

            //Skip IRP Review option only shown to vivli admin in certain statuses,
            //disappears after the IRP review
            //read the IRP option selection if we are allowed to change them at this time,
            //otherwise leave them as they are
            if (skipOptionSelected) {
                isBypassIrpReview = true;
            } else if (displayIrpReviewSelection(dataRequest)) {
                isBypassIrpReview = false;
            }
        }

        const reqStudyMods: IRequestedStudyModification = {
            dataRequestId: dataRequest.id,
            studyId: requestedStudy.studyId,
            thisRequestOnlyIpd: isThisRequestOnly,
            dataPackageDownloadable: isDownloadable,
            skipIrpReview: isBypassIrpReview,
        };
        return reqStudyMods;
    };

    return {
        getStatusMessage,
        getStudyIdForDisplay,
        canEditAdvancedOptions,
        getOptionsToModify,
        optionsConstants,
        canShowAdvancedOptionsStatus,
        canChangeIrpReview,
        optionOnTitlesConstants,
        optionOffTitlesConstants,
    };
}
